<template>
    <v-card class="rounded-lg px-3 py-3 leading-card">
        <div class="leading-subtitle">Κορυφαίοι ανά κατηγορία</div>

        <div v-if="!hasData" class="leading-empty">
            <v-icon color="orange lighten-1">mdi-podium</v-icon>
            Οι κορυφαίοι θα εμφανιστούν μόλις καταγραφούν στατιστικά.
        </div>
        <svg v-else class="leading-svg" viewBox="0 0 920 760" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Leading players by category radar" style="min-height: 0;">
            <defs>
                <clipPath :id="avatarClipId">
                    <circle cx="0" cy="0" r="34" />
                </clipPath>
            </defs>

            <g :transform="chartTransform">
                <g>
                    <path
                        v-for="slice in radarSlices"
                        :key="slice.key"
                        :d="slice.path"
                        :fill="slice.color"
                    />
                    <circle :cx="center.x" :cy="center.y" :r="innerRadius" fill="#f7f9fc" stroke="#d6ddeb" stroke-width="2" />

                    <text
                        v-for="label in coreLabels"
                        :key="label.key"
                        :x="label.x"
                        :y="label.y"
                        class="core-label custom-orange-text"
                        text-anchor="middle"
                        dominant-baseline="middle"
                        :transform="`rotate(${label.rotation} ${label.x} ${label.y})`"
                    >
                        {{ label.text }}
                    </text>
                </g>

                <g v-for="edge in edges" :key="edge.key">
                    <polyline
                        :points="`${edge.sliceX},${edge.sliceY} ${edge.statX},${edge.statY}`"
                        :stroke="edge.color"
                        stroke-width="2.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />

                    <!-- a player only for a category he leads with a number (no stats: no player) -->
                    <template v-if="edge.hasPlayer">
                        <polyline
                            :points="`${edge.statX},${edge.statY} ${edge.connectorElbowX},${edge.connectorY} ${edge.connectorTargetX},${edge.connectorY}`"
                            :stroke="edge.color"
                            stroke-width="2.5"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />

                        <circle
                            :cx="edge.statX"
                            :cy="edge.statY"
                            r="16"
                            fill="#ffffff"
                            :stroke="edge.color"
                            stroke-width="2.6"
                        />
                        <text
                            :x="edge.statX"
                            :y="edge.statY"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            class="stat-label"
                        >
                            {{ edge.value }}
                        </text>

                        <circle
                            :cx="edge.playerX"
                            :cy="edge.playerY"
                            r="34"
                            fill="#ffffff"
                            :stroke="edge.color"
                            stroke-width="2.3"
                            class="player-click-target"
                            @click="goToPlayer(edge.playerId)"
                        />
                        <image
                            :href="edge.image"
                            x="-34"
                            y="-34"
                            width="68"
                            height="68"
                            :transform="`translate(${edge.playerX}, ${edge.playerY})`"
                            :clip-path="`url(#${avatarClipId})`"
                            preserveAspectRatio="xMidYMid meet"
                            class="player-click-target"
                            @click="goToPlayer(edge.playerId)"
                        />

                        <line
                            :x1="edge.nameLineStartX"
                            :x2="edge.nameLineEndX"
                            :y1="edge.nameLineY"
                            :y2="edge.nameLineY"
                            :stroke="edge.color"
                            stroke-width="2"
                        />
                        <text
                            :x="edge.playerX"
                            :y="edge.captionStartY"
                            text-anchor="middle"
                            class="name-label secondary-font"
                            :class="{ 'player-click-target': !!edge.playerId }"
                            @click="goToPlayer(edge.playerId)"
                        >
                            <tspan :x="edge.playerX" class="jersey-label">{{ edge.jersey }}</tspan>
                            <tspan v-if="edge.lastName" :x="edge.playerX" :dy="nameLineStep">{{ edge.lastName }}</tspan>
                            <tspan v-if="edge.firstName" :x="edge.playerX" :dy="nameLineStep">{{ edge.firstName }}</tspan>
                        </text>
                    </template>
                </g>
            </g>
        </svg>
    </v-card>
</template>

<script>
    import { getTeamColors } from '@/utils/shotChart'

    export default {
        name: 'LeadingPlayersGraph',
        props: {
            match: {
                type: Object,
                required: true,
                default: () => ({})
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                center: { x: 460, y: 350 },
                innerRadius: 62,
                maxOuterRadius: 126,
                viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200
            }
        },
        computed: {
            isMobile() {
                return this.viewportWidth <= 960
            },
            avatarClipId() {
                return `leading-player-clip-${this._uid}`
            },
            teamColors() {
                return getTeamColors(this.match?.team1, this.match?.team2)
            },
            team1Color() {
                return this.teamColors.team1
            },
            team2Color() {
                return this.teamColors.team2
            },
            desiredChartScale() {
                return this.isMobile ? 1.56 : 1.38
            },
            fitChartScale() {
                const canvasWidth = 920
                const canvasHeight = 760
                const padding = 20
                const topPlayerOffset = Math.max(0, -this.playerYOffset)
                const bottomPlayerOffset = Math.max(0, this.playerYOffset)

                // Estimate furthest drawable extents from chart center.
                const leftReach = this.playerRadius + this.nameLineHalfWidth + this.connectorInset + 28
                const rightReach = this.playerRadius + this.nameLineHalfWidth + this.connectorInset + 28
                const topReach = this.playerRadius + 38 + topPlayerOffset
                const bottomReach = this.playerRadius + this.nameLineOffset + 26 + bottomPlayerOffset

                const scaleLeft = (this.center.x - padding) / leftReach
                const scaleRight = (canvasWidth - this.center.x - padding) / rightReach
                const scaleTop = (this.center.y - padding) / topReach
                const scaleBottom = (canvasHeight - this.center.y - padding) / bottomReach

                return Math.max(0.85, Math.min(scaleLeft, scaleRight, scaleTop, scaleBottom))
            },
            chartScale() {
                return Math.min(this.desiredChartScale, this.fitChartScale)
            },
            playerRadius() {
                return this.isMobile ? 226 : 258
            },
            playerYOffset() {
                return this.isMobile ? -14 : -8
            },
            captionOffset() {
                return this.isMobile ? 50 : 54
            },
            // under the jersey and the two name lines (sizes follow the larger labels)
            nameLineOffset() {
                return this.isMobile ? 92 : 100
            },
            nameLineStep() {
                return this.isMobile ? 17 : 19
            },
            nameLineHalfWidth() {
                return this.isMobile ? 52 : 60
            },
            connectorInset() {
                return this.isMobile ? 18 : 24
            },
            chartTransform() {
                const scale = this.chartScale
                const liftY = 0
                return `translate(${this.center.x} ${this.center.y}) scale(${scale}) translate(${-this.center.x} ${-this.center.y}) translate(0 ${liftY})`
            },
            categories() {
                return [
                    { key: 'blockedShots', label: 'ΚΟΨ', angle: -135 },
                    { key: 'points', label: 'ΠΟΝ', angle: -45 },
                    { key: 'rebounds', label: 'ΡΙΜ', angle: 45 },
                    { key: 'assists', label: 'ΑΣΙ', angle: 135 }
                ]
            },
            entries() {
                const pairHalfWidth = 45
                const teamSliceWidth = pairHalfWidth
                const list = []

                this.categories.forEach(category => {
                    const leader1 = this.getLeader(this.match?.team1, category.key)
                    const leader2 = this.getLeader(this.match?.team2, category.key)

                    const team1Start = category.angle - pairHalfWidth
                    const team1End = team1Start + teamSliceWidth
                    const team2Start = team1End
                    const team2End = team2Start + teamSliceWidth

                    list.push({
                        key: `${category.key}-team1`,
                        category,
                        team: 1,
                        color: this.team1Color,
                        playerId: leader1.playerId,
                        value: leader1.value,
                        jersey: leader1.jersey,
                        firstName: leader1.firstName,
                        lastName: leader1.lastName,
                        image: leader1.image,
                        midAngle: (team1Start + team1End) / 2,
                        startAngle: team1Start,
                        endAngle: team1End
                    })

                    list.push({
                        key: `${category.key}-team2`,
                        category,
                        team: 2,
                        color: this.team2Color,
                        playerId: leader2.playerId,
                        value: leader2.value,
                        jersey: leader2.jersey,
                        firstName: leader2.firstName,
                        lastName: leader2.lastName,
                        image: leader2.image,
                        midAngle: (team2Start + team2End) / 2,
                        startAngle: team2Start,
                        endAngle: team2End
                    })
                })

                return list
            },
            // some player of either team has a number in some category
            hasData() {
                return this.entries.some(entry => Number(entry.value) > 0)
            },
            maxValue() {
                const values = this.entries.map(entry => entry.value)
                return Math.max(1, ...values)
            },
            radarSlices() {
                return this.entries.map(entry => {
                    const outer = this.outerRadius(entry.value)
                    return {
                        key: `${entry.key}-slice`,
                        color: entry.color,
                        path: this.sectorPath(this.center.x, this.center.y, this.innerRadius, outer, entry.startAngle, entry.endAngle)
                    }
                })
            },
            coreLabels() {
                const labelRadius = 36
                return this.categories.map(category => {
                    const point = this.polarPoint(this.center.x, this.center.y, labelRadius, category.angle)
                    let rotation = category.angle + 90
                    // Keep text orientation readable left-to-right.
                    if (rotation > 90) rotation -= 180
                    if (rotation < -90) rotation += 180
                    return {
                        key: category.key,
                        text: category.label,
                        x: point.x,
                        y: point.y,
                        rotation
                    }
                })
            },
            edges() {
                return this.entries.map(entry => {
                    const outer = this.outerRadius(entry.value)
                    const slicePoint = this.polarPoint(this.center.x, this.center.y, outer - 2, entry.midAngle)
                    const statPoint = this.polarPoint(this.center.x, this.center.y, Math.min(outer + 8, this.maxOuterRadius + 10), entry.midAngle)
                    const playerPointRaw = this.polarPoint(this.center.x, this.center.y, this.playerRadius, entry.midAngle)
                    const playerPoint = {
                        x: playerPointRaw.x,
                        y: playerPointRaw.y + this.playerYOffset
                    }
                    const captionStartY = playerPoint.y + this.captionOffset
                    const nameLineY = playerPoint.y + this.nameLineOffset
                    const nameLineStartX = playerPoint.x - this.nameLineHalfWidth
                    const nameLineEndX = playerPoint.x + this.nameLineHalfWidth
                    const isLeft = playerPoint.x < this.center.x
                    const connectorTargetX = isLeft ? nameLineEndX : nameLineStartX
                    const connectorElbowX = connectorTargetX + (isLeft ? this.connectorInset : -this.connectorInset)
                    const connectorY = nameLineY

                    return {
                        key: `${entry.key}-edge`,
                        color: entry.color,
                        playerId: entry.playerId,
                        value: entry.value,
                        jersey: entry.jersey,
                        firstName: entry.firstName,
                        lastName: entry.lastName,
                        image: entry.image,
                        sliceX: slicePoint.x,
                        sliceY: slicePoint.y,
                        statX: statPoint.x,
                        statY: statPoint.y,
                        playerX: playerPoint.x,
                        playerY: playerPoint.y,
                        captionStartY,
                        nameLineStartX,
                        nameLineEndX,
                        nameLineY,
                        connectorTargetX,
                        connectorElbowX,
                        connectorY,
                        hasPlayer: !!entry.playerId && Number(entry.value) > 0
                    }
                })
            }
        },
        mounted() {
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', this.onResize)
                this.onResize()
            }
        },
        beforeDestroy() {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', this.onResize)
            }
        },
        methods: {
            onResize() {
                this.viewportWidth = window.innerWidth
            },
            degToRad(deg) {
                return (deg * Math.PI) / 180
            },
            polarPoint(cx, cy, radius, angleDeg) {
                const angle = this.degToRad(angleDeg)
                return {
                    x: cx + (Math.cos(angle) * radius),
                    y: cy + (Math.sin(angle) * radius)
                }
            },
            outerRadius(value) {
                const ratio = value / this.maxValue
                const range = this.maxOuterRadius - this.innerRadius
                const minVisibleRatio = 0.18
                const easedRatio = Math.pow(Math.max(0, Math.min(1, ratio)), 0.9)
                const adjustedRatio = minVisibleRatio + (easedRatio * (1 - minVisibleRatio))
                return this.innerRadius + (adjustedRatio * range)
            },
            sectorPath(cx, cy, innerRadius, outerRadius, startDeg, endDeg) {
                const startOuter = this.polarPoint(cx, cy, outerRadius, startDeg)
                const endOuter = this.polarPoint(cx, cy, outerRadius, endDeg)
                const endInner = this.polarPoint(cx, cy, innerRadius, endDeg)
                const startInner = this.polarPoint(cx, cy, innerRadius, startDeg)
                const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0

                return `M ${startOuter.x} ${startOuter.y} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y} L ${endInner.x} ${endInner.y} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${startInner.x} ${startInner.y} Z`
            },
            getLeader(team = {}, statKey) {
                const players = Array.isArray(team?.players) ? team.players : []
                if (!players.length) {
                    return {
                        playerId: null,
                        jersey: '#--',
                        firstName: 'Player',
                        lastName: 'Pending',
                        value: 0,
                        image: this.defaultPlayerImg
                    }
                }

                const top = players.reduce((best, player) => {
                    const current = this.statValue(player, statKey)
                    const bestValue = this.statValue(best, statKey)
                    return current > bestValue ? player : best
                }, players[0])

                return {
                    playerId: top?.id || top?.playerId || top?.athleteId || top?.player?.id || top?.athlete?.id || null,
                    ...this.playerCaption(top),
                    value: Math.round(this.statValue(top, statKey)),
                    image: this.playerImage(top)
                }
            },
            goToPlayer(playerId) {
                if (!playerId) return
                this.$router.push({ name: 'Player', params: { id: playerId } })
            },
            statValue(player = {}, statKey) {
                return Number(player?.stats?.[0]?.[statKey]) || 0
            },
            playerCaption(player = {}) {
                const jersey = player?.alternativeJersey ?? player?.jersey ?? player?.player?.jersey ?? player?.athlete?.jersey
                const directFirst =
                    player?.firstName ||
                    player?.givenName ||
                    player?.athleteFirstName ||
                    player?.player?.firstName ||
                    player?.athlete?.firstName ||
                    ''
                const directLast =
                    player?.lastName ||
                    player?.surname ||
                    player?.familyName ||
                    player?.athleteLastName ||
                    player?.player?.lastName ||
                    player?.player?.surname ||
                    player?.athlete?.lastName ||
                    ''
                const raw =
                    player?.athleteName ||
                    player?.name ||
                    player?.playerName ||
                    `${player?.firstName || ''} ${player?.lastName || ''}`.trim() ||
                    `${player?.player?.firstName || ''} ${player?.player?.lastName || ''}`.trim()

                const safe = String(raw || '').trim()
                if (!safe) {
                    return {
                        jersey: jersey !== null && jersey !== undefined && jersey !== '' ? `#${String(jersey).trim()}` : '#--',
                        firstName: 'Player',
                        lastName: 'Pending'
                    }
                }

                const parts = safe.split(/\s+/)
                const fallbackFirst = parts[0] || ''
                const fallbackLast = parts.length > 1 ? parts[parts.length - 1] : ''

                const first = String(directFirst || fallbackFirst).trim()
                const last = String(directLast || fallbackLast).trim()

                const toTitle = value => value.charAt(0).toLocaleUpperCase() + value.slice(1).toLocaleLowerCase()
                return {
                    jersey: jersey !== null && jersey !== undefined && jersey !== '' ? `#${String(jersey).trim()}` : '#--',
                    firstName: first ? toTitle(first) : '',
                    lastName: last ? toTitle(last) : ''
                }
            },
            playerImage(player = {}) {
                const raw = player?.image || player?.photo || player?.avatar || player?.imageUrl || player?.player?.image || player?.athlete?.image || null
                if (!raw) return this.defaultPlayerImg
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            }
        }
    }
</script>

<style scoped>
.leading-card {
    overflow: visible;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.leading-subtitle {
    padding: 4px 6px 0;
    color: rgba(0, 0, 0, 0.5);
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.leading-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 220px;
    padding: 24px;
    color: rgba(0, 0, 0, 0.5);
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    text-align: center;
}

.leading-svg {
    width: 100%;
    height: auto;
    aspect-ratio: 920 / 760;
    max-height: 82vh;
    display: block;
}

.core-label {
    fill: currentColor;
    font-size: 17px;
    font-weight: 900;
    font-family: Ubuntu, Arial, sans-serif;
}

.stat-label {
    fill: #000;
    font-size: 16px;
    font-weight: 800;
    font-family: Ubuntu, Arial, sans-serif;
}

.name-label {
    fill: #111;
    font-size: 18px;
    font-weight: 700;
    font-family: Ubuntu, Arial, sans-serif !important;
}

.jersey-label {
    font-size: 15px;
    font-weight: 800;
}

.player-click-target {
    cursor: pointer;
}

@media only screen and (max-width: 960px) {
    .leading-card {
        padding: 8px !important;
    }

    .leading-svg {
        max-height: 70vh;
    }

    .name-label {
        font-size: 16px;
    }
}
</style>
